import React, { memo } from "react";
import { FiExternalLink } from "react-icons/fi";
import { NavLink } from "react-router-dom";

import { NavItemType } from "../Navbar";
import { stripTrailingSlash } from "../navActive";
interface ElementNavBarProps {
  navItem: NavItemType;
  onLinkClick?: () => void;
  open?: boolean;
  showLinkLogo?: boolean;
  toggleNavbar?: () => void;
  /**
   * When provided by the navbar (the single longest-matching href across all
   * items), this item takes full control of its active state from it instead of
   * relying on NavLink's per-link prefix match — which lights up every ancestor
   * item at once (e.g. "Articles" staying active on `/app/blog/media`).
   */
  activeHref?: string;
}

const ElementNavBar: React.FC<ElementNavBarProps> = ({
  navItem,
  onLinkClick,
  open = true,
  showLinkLogo = true,
  toggleNavbar,
  activeHref,
}) => {
  const handleLinkClick = () => {
    if (navItem.link && toggleNavbar) {
      toggleNavbar();
    }
    if (onLinkClick) {
      onLinkClick();
    }
  };

  // Home route: either "/" or "/:lang/" — needs exact match to avoid matching everything
  const isHomeRoute =
    navItem.href === "/" || /^\/[a-z]{2}\/?$/.test(navItem.href ?? "");

  // When the navbar resolved the active href, drive the active state from it
  // (longest-prefix wins) and override NavLink's own per-link matching.
  const controlled = activeHref !== undefined;
  const forcedActive =
    controlled &&
    navItem.href !== undefined &&
    stripTrailingSlash(navItem.href) === stripTrailingSlash(activeHref);

  return (
    <li className={`nav-item ${navItem.className ?? ""}`}>
      <NavLink
        to={navItem.href ?? navItem.link ?? "/"}
        className={
          controlled ? () => `nav-link${forcedActive ? " active" : ""}` : `nav-link`
        }
        onClick={handleLinkClick}
        aria-current={controlled ? (forcedActive ? "page" : undefined) : "page"}
        end={controlled ? undefined : isHomeRoute}
        target={navItem.link ? "_blank" : "_self"}
      >
        {navItem.icon && navItem.icon}
        {open && (
          <span className="link-text">
            <span className="link-label">{navItem.text}</span>
            {navItem.link && showLinkLogo && (
              <FiExternalLink className="link-externalIcon" />
            )}
            {navItem.badge != null && (
              <span className="navBadge">{navItem.badge}</span>
            )}
          </span>
        )}
      </NavLink>
    </li>
  );
};

export default memo(ElementNavBar);
